import ChevronRight from '~icons/hawk/chevron-right?raw';
import OutlineGroups from '~icons/ic/outline-groups?raw';
import chroma from 'chroma-js';
import { keyBy } from 'lodash-es';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { getPriorityObject } from '~/common/composables/kanban-helpers.js';
import { changeIconDimensions, stringToNumber } from '~/common/utils/common.utils.js';
import { useTasksStore } from '~/tasks/store/tasks.store';

export function useCustomRenders() {
  const { common_store } = useCommonImports();

  // All these renderers are read only
  function breadcrumbRenderer(instance, td, row, col, prop, value, cellProperties) {
    td.innerHTML = '';

    const physical_row = instance.toPhysicalRow(row);
    const row_data = instance.getSourceDataAtRow?.(physical_row);

    const { value: display_value, path = [] } = row_data?.[cellProperties.breadcrumb_data_key] || {};

    if (!display_value)
      return td;

    const div = document.createElement('div');
    div.classList.add('flex', 'justify-center', 'flex-col');

    const bread_crumbs_container = document.createElement('div');
    bread_crumbs_container.classList.add('flex', 'items-center', 'gap-0.5', 'text-xs', 'font-medium', 'text-gray-500');

    const formatted_path = path.filter(item => item?.length);

    for (const item of formatted_path) {
      const item_element = document.createElement('div');
      item_element.textContent = item;
      bread_crumbs_container.appendChild(item_element);

      if (item !== formatted_path[formatted_path.length - 1]) {
        const separator = document.createElement('div');
        separator.classList.add('flex', 'items-center');
        separator.innerHTML = changeIconDimensions(ChevronRight, 12, 12);
        bread_crumbs_container.appendChild(separator);
      }
    }

    const name_element = document.createElement('div');
    name_element.classList.add('text-[13px]', 'text-gray-900');
    name_element.textContent = display_value;

    if (path.length) {
      div.appendChild(bread_crumbs_container);
      name_element.classList.add('mt-1');
    }

    div.appendChild(name_element);

    td.appendChild(div);

    return td;
  }

  const teams_name_map = computed(() => keyBy(common_store.teams, 'name'));

  function getTag(name, type = 'tag') {
    const is_team = !!teams_name_map.value[name];

    const tags_container = document.createElement('div');
    tags_container.classList.add('flex', 'gap-x-1', 'w-fit', 'items-center', 'bg-gray-100', 'rounded-lg', 'text-xs', 'font-medium', 'pr-2', 'py-0.75', 'pl-1', 'bi-exportable-tag');

    if (type === 'member') {
      if (is_team) {
        const team_icon = document.createElement('span');
        team_icon.classList.add('text-blue-500', 'inline-flex', 'mr-[6px]');
        team_icon.innerHTML = changeIconDimensions(OutlineGroups, 16, 16);
        tags_container.appendChild(team_icon);
      }
      else {
        const user_icon = document.createElement('span');
        user_icon.classList.add('w-4', 'h-4', 'rounded-full', 'flex', 'items-center', 'justify-center', 'text-white');
        user_icon.style.backgroundColor = stringToNumber(name);
        const text_node = document.createElement('div');
        text_node.textContent = name.charAt(0);
        user_icon.appendChild(text_node);
        tags_container.appendChild(user_icon);
      }
    }
    if (type === 'tag') {
      tags_container.classList.add('border-gray-200', 'border', 'p-1', 'bg-transparent');
    }

    const name_field = document.createElement('div');
    name_field.textContent = name;
    tags_container.appendChild(name_field);

    return tags_container;
  }

  function tagsRenderer(instance, td, row, col, prop, value, cellProperties) {
    td.innerHTML = '';

    const tag_type = cellProperties?.tag_type || 'tag';
    const formatted_value = Array.isArray(value) ? value : (value || '').split(', ').filter(v => v.length);

    if (formatted_value.length === 0)
      return td;

    const tags_container = document.createElement('div');
    tags_container.className = 'flex items-center gap-1';

    const member_tag = getTag(formatted_value[0], tag_type);
    tags_container.appendChild(member_tag);

    if (formatted_value.length > 1) {
      const addition_tag = getTag(`+${formatted_value.length - 1}`, 'tag');
      if (tag_type === 'member') {
        addition_tag.classList.add('bg-gray-100');
        addition_tag.classList.remove('bg-transparent', 'border');
      }
      addition_tag.classList.add('cursor-pointer');

      const popup = instance.rootElement.querySelector(`[data-popup-id="${row}-${col}"]`)
        || (() => {
          const el = document.createElement('div');
          el.dataset.popupId = `${row}-${col}`;
          el.className = `
            absolute bg-white border border-gray-300 shadow p-2 rounded
            hidden z-[99999] flex flex-col gap-y-2 rounded-lg max-h-60 min-w-[100px]
          `;
          instance.rootElement.appendChild(el);
          return el;
        })();

      popup.innerHTML = ''; // reset
      formatted_value.slice(1).forEach((member) => {
        popup.appendChild(getTag(member, tag_type));
      });

      let popupTimeout;
      function showPopup() {
        clearTimeout(popupTimeout);

        const tagRect = addition_tag.getBoundingClientRect();
        const rootRect = instance.rootElement.getBoundingClientRect();

        const left = (tagRect.left - rootRect.left);
        const top = tagRect.bottom - rootRect.top + 4; // 4px spacing
        popup.style.transform = 'translateX(calc(-100% + 25px))';

        popup.style.left = `${left}px`;
        popup.style.top = `${top}px`;

        popup.classList.remove('hidden');
      }

      function hidePopup() {
        popupTimeout = setTimeout(() => {
          popup.classList.add('hidden');
        }, 300);
      }

      addition_tag.addEventListener('mouseover', showPopup);
      addition_tag.addEventListener('mouseout', hidePopup);

      // Also keep popup open when hovering over the popup itself
      popup.addEventListener('mouseover', showPopup);
      popup.addEventListener('mouseout', hidePopup);

      tags_container.appendChild(addition_tag);
    }

    td.appendChild(tags_container);
    return td;
  }

  function priorityRenderer(instance, td, row, col, prop, value) {
    td.innerHTML = '';
    const priority_object = getPriorityObject('label')[value || 'Not set'];

    if (!priority_object)
      return td;

    const priority_container = document.createElement('div');
    priority_container.className = `flex items-center ${priority_object.priority_class_icon}`;

    const icon = document.createElement('span');
    icon.className = 'flex items-center';
    icon.innerHTML = priority_object.IconToBeDisplayed;

    priority_container.appendChild(icon);

    const label = document.createElement('span');
    label.className = `flex items-center pl-1 text-[13px] ${priority_object.priority_class_label}`;
    label.textContent = priority_object.label;

    priority_container.appendChild(label);

    td.appendChild(priority_container);

    return td;
  }

  function statusRenderer(instance, td, row, col, prop, value, cellProperties) {
    td.innerHTML = '';

    const module = cellProperties?.module || 'tasks';
    let status_config = null;

    if (module === 'tasks') {
      status_config = useTasksStore().status_values.find(item => item.label === value);
    }

    if (status_config) {
      const status_container = document.createElement('div');
      status_container.className = 'content-center rounded-full px-2 py-1 inline-flex text-xs whitespace-nowrap font-medium';
      status_container.style.color = status_config.color;
      status_container.style.backgroundColor = chroma(status_config.color).alpha(0.1).css();
      status_container.textContent = status_config.label;
      td.appendChild(status_container);
    }
    else {
      td.textContent = value;
    }

    return td;
  }

  function progressRenderer(instance, td, row, _col, _prop, value) {
    td.innerHTML = '';

    const physical_row = instance.toPhysicalRow(row);
    const row_data = instance.getSourceDataAtRow?.(physical_row) || {};

    const actual_raw = value == null ? 0 : Number(value);
    const actual_fraction = (Number.isNaN(actual_raw) ? 0 : (actual_raw > 1 ? actual_raw / 100 : actual_raw));

    let planned_raw;
    if (row_data['Activities ⇒ Planned progress'])
      planned_raw = row_data['Activities ⇒ Planned progress'];

    const container = document.createElement('div');
    container.className = 'flex items-center';

    const actual_percent = Math.round(actual_fraction * 100);
    const actual_el = document.createElement('div');
    actual_el.className = 'text-xs text-gray-700 w-10 font-medium';
    actual_el.textContent = `${actual_percent}%`;
    container.appendChild(actual_el);

    // Only show the difference chip when planned is present and parseable
    if (planned_raw != null && planned_raw !== '') {
      let planned_num = Number(planned_raw);
      if (Number.isNaN(planned_num)) {
        const m = String(planned_raw).match(/(-?\d+\.?\d*)/);
        planned_num = m ? Number(m[1]) : Number.NaN;
      }
      if (!Number.isNaN(planned_num)) {
        if (planned_num > 1)
          planned_num = planned_num / 100;
        const diff = Math.round((actual_fraction - planned_num) * 100);

        const chip = document.createElement('div');
        chip.className = 'text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center justify-center';

        if (diff > 0) {
          chip.style.color = '#166534';
          chip.style.backgroundColor = 'rgba(16,185,129,0.12)';
          chip.textContent = `+${diff}%`;
        }
        else if (diff < 0) {
          chip.style.color = '#991b1b';
          chip.style.backgroundColor = 'rgba(239,68,68,0.12)';
          chip.textContent = `${diff}%`;
        }

        container.appendChild(chip);
      }
    }

    td.appendChild(container);
    return td;
  }

  return {
    breadcrumbRenderer,
    tagsRenderer,
    priorityRenderer,
    statusRenderer,
    progressRenderer,
  };
}
